import { ApiClient } from './client';
import type { ApiEnvelope, ApiResponse } from './types';
import { unwrapApiResponseData } from './types';
import { translateClient } from '@/lib/i18n/client';
import type { MaterialUploadCheckpoint } from './materials';
import type {
  CreateSubjectModelRequest,
  CreateSoulCharacterRequest,
  SoulCharacterQuote,
  SoulSaveSession,
  SubjectModel,
  SubjectModelType,
  SubjectModelAnchorImage,
  UpdateSubjectModelRequest,
} from '@/types/subject-model';

interface SuccessEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class SubjectModelsApi extends ApiClient {
  private unwrap<T>(response: ApiResponse<SuccessEnvelope<T> | T>): ApiResponse<T> {
    return unwrapApiResponseData<T>(response as ApiResponse<ApiEnvelope<T> | T>, {
      code: 'API_ERROR',
      message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  async list(modelType?: SubjectModelType): Promise<ApiResponse<SubjectModel[]>> {
    const qs = modelType ? `?model_type=${modelType}` : '';
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel[]>>(`/subject-models${qs}`));
  }

  async get(id: string): Promise<ApiResponse<SubjectModel>> {
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel>>(`/subject-models/${id}`));
  }

  async create(payload: CreateSubjectModelRequest): Promise<ApiResponse<SubjectModel>> {
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel>>('/subject-models', {
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  }

  async settleKreaBill(id: string): Promise<ApiResponse<{ billing_status: string; credits: number | null; payment_error?: string }>> {
    return this.unwrap(await this.request<SuccessEnvelope<{ billing_status: string; credits: number | null; payment_error?: string }>>(
      `/subject-models/krea/${encodeURIComponent(id)}/settle`, { method: 'POST' }, false,
    ));
  }

  async soulQuote(): Promise<ApiResponse<SoulCharacterQuote>> {
    return this.unwrap(await this.request<SuccessEnvelope<SoulCharacterQuote>>('/subject-models/soul-id/quote'));
  }

  async soulVideoPhotos(sourceAssetId: string, signal?: AbortSignal): Promise<ApiResponse<{ source_asset_id: string; photos: SubjectModelAnchorImage[] }>> {
    return this.unwrap(await this.request<SuccessEnvelope<{ source_asset_id: string; photos: SubjectModelAnchorImage[] }>>('/subject-models/soul-id/video-photos', {
      method: 'POST', body: JSON.stringify({ source_asset_id: sourceAssetId }), signal,
    }, false));
  }

  async createSoul(payload: CreateSoulCharacterRequest): Promise<ApiResponse<SubjectModel>> {
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel>>('/subject-models/soul-id', {
      method: 'POST', body: JSON.stringify(payload),
    }, false));
  }

  async findSoulRequest(requestId: string): Promise<ApiResponse<SubjectModel | null>> {
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel | null>>(
      `/subject-models/soul-id/requests/${encodeURIComponent(requestId)}`,
    ));
  }

  async startSoulSave(payload: { request_id: string; name: string; accepted_credits: number;
    photos: { photo_id: string; asset_id?: string }[] }): Promise<ApiResponse<SoulSaveSession>> {
    return this.unwrap(await this.request<SuccessEnvelope<SoulSaveSession>>('/subject-models/soul-id/saves', {
      method: 'POST', body: JSON.stringify(payload),
    }, false));
  }

  async getSoulSave(id: string): Promise<ApiResponse<SoulSaveSession | null>> {
    return this.unwrap(await this.request<SuccessEnvelope<SoulSaveSession | null>>(`/subject-models/soul-id/saves/${encodeURIComponent(id)}`));
  }

  async resumeSoulSave(id: string, acceptedCredits: number): Promise<ApiResponse<SoulSaveSession>> {
    return this.unwrap(await this.request<SuccessEnvelope<SoulSaveSession>>(`/subject-models/soul-id/saves/${encodeURIComponent(id)}/resume`, {
      method: 'POST', body: JSON.stringify({ accepted_credits: acceptedCredits }),
    }, false));
  }

  async registerSoulSavePhoto(id: string, index: number, assetId: string, checkpoint?: MaterialUploadCheckpoint): Promise<ApiResponse<SoulSaveSession>> {
    return this.unwrap(await this.request<SuccessEnvelope<SoulSaveSession>>(`/subject-models/soul-id/saves/${encodeURIComponent(id)}/photos/${index}`, {
      method: 'PUT', body: JSON.stringify({ asset_id: assetId, checkpoint }),
    }, false));
  }

  async cancelSoulSave(id: string): Promise<ApiResponse<SoulSaveSession>> {
    return this.unwrap(await this.request<SuccessEnvelope<SoulSaveSession>>(`/subject-models/soul-id/saves/${encodeURIComponent(id)}`, { method: 'DELETE' }, false));
  }

  async update(id: string, payload: UpdateSubjectModelRequest): Promise<ApiResponse<SubjectModel>> {
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel>>(`/subject-models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }));
  }

  async archive(id: string): Promise<ApiResponse<{ id: string; status: 'archived' }>> {
    return this.unwrap(await this.request<SuccessEnvelope<{ id: string; status: 'archived' }>>(`/subject-models/${id}`, {
      method: 'DELETE',
    }));
  }

  async retryAnalysis(id: string): Promise<ApiResponse<SubjectModel>> {
    return this.unwrap(await this.request<SuccessEnvelope<SubjectModel>>(`/subject-models/${id}/retry-analysis`, {
      method: 'POST',
    }));
  }
}

export const subjectModelsApi = new SubjectModelsApi();
export { SubjectModelsApi };
